import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Work as WorkIcon,
  ArrowForward as ArrowForwardIcon,
  Business as BusinessIcon,
  Share as ShareIcon,
  ContentCopy as CopyIcon,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import JobService from '../services/JobService';
import { formatWhatsAppTime, formatJobTimestamp } from '../utils/dateUtils';
import useUserType from '../hooks/useUserType';
import { usePostHog } from '@posthog/react';
import { jobsCache } from '../utils/cacheUtils';


// Reusable Share Menu Component
const ShareMenu = ({ job, anchorEl, open, onClose, onSnackbar }) => {
  const posthog = usePostHog();
  const jobUrl = `${window.location.origin}/jobs/${job.id}/apply`;
  const jobTitle = job.referenceCode || `Job #${job.id?.slice(-8) || 'Unknown'}`;
  const jobText = `Check out this ${jobTitle} position${job.company ? ` at ${job.company}` : ''}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(jobUrl);
      onSnackbar('Job link copied to clipboard!');
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = jobUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      onSnackbar('Job link copied to clipboard!');
    }
    posthog?.capture('job_link_copied', { job_id: job.id });
    onClose();
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: jobTitle,
          text: jobText,
          url: jobUrl
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          handleCopyLink();
        }
      }
    } else {
      handleCopyLink();
    }
    onClose();
  };

  const handleWhatsAppShare = () => {
    const whatsappText = encodeURIComponent(`${jobText}\n\n${jobUrl}`);
    window.open(`https://wa.me/?text=${whatsappText}`, '_blank');
    posthog?.capture('job_shared_whatsapp', { job_id: job.id });
    onClose();
  };



  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      <MenuItem onClick={handleNativeShare}>
        <ListItemIcon>
          <ShareIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Share</ListItemText>
      </MenuItem>
      
      <MenuItem onClick={handleCopyLink}>
        <ListItemIcon>
          <CopyIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Copy Link</ListItemText>
      </MenuItem>
      
      <MenuItem onClick={handleWhatsAppShare}>
        <ListItemIcon>
          <WhatsAppIcon fontSize="small" sx={{ color: '#25D366' }} />
        </ListItemIcon>
        <ListItemText>WhatsApp</ListItemText>
      </MenuItem>
    </Menu>
  );
};

// WhatsApp-style Job Card Component
const WhatsAppJobCard = ({ job, isSuspended }) => {
  const navigate = useNavigate();
  const posthog = usePostHog();
  const [shareAnchorEl, setShareAnchorEl] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [suspendedDialogOpen, setSuspendedDialogOpen] = useState(false);

  const handleApply = () => {
    if (isSuspended) {
      setSuspendedDialogOpen(true);
      return;
    }
    posthog?.capture('job_card_clicked', { job_id: job.id, job_reference: job.referenceCode, card_type: 'whatsapp' });
    navigate(`/jobs/${job.id}/apply`);
  };

  const handleShareClick = (event) => {
    setShareAnchorEl(event.currentTarget);
  };

  const handleShareClose = () => {
    setShareAnchorEl(null);
  };

  const handleSnackbar = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        borderRadius: 2,
        p: 2,
        mb: 2,
        border: '1px solid',
        borderColor: 'divider',
        minHeight: '300px', // Consistent minimum height
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* WhatsApp Message Bubble */}
      <Box
        sx={{
          backgroundColor: 'background.paper',
          borderRadius: '18px 18px 18px 4px',
          padding: '12px 16px',
          position: 'relative',
          maxWidth: '95%',
          boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
          border: 1,
          borderColor: 'divider',
          mb: 2,
          '&::before': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: '-6px',
            width: 0,
            height: 0,
            borderTop: '6px solid transparent',
            borderRight: (t) => `6px solid ${t.palette.background.paper}`,
            borderBottom: '6px solid transparent',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -1,
            left: '-7px',
            width: 0,
            height: 0,
            borderTop: '7px solid transparent',
            borderRight: (t) => `7px solid ${t.palette.divider}`,
            borderBottom: '7px solid transparent',
            zIndex: -1
          }
        }}
      >
        {/* Job Header inside bubble */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="body2" fontWeight="600" color="primary.main" sx={{ fontSize: '12px' }}>
            WhatsApp Job
          </Typography>
        </Box>
        
        {/* Job ID as title since title field is not populated */}
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600,
            fontSize: '16px',
            color: 'text.primary',
            mb: 1
          }}
        >
          {job.referenceCode || `Job #${job.id?.slice(-8) || 'Unknown'}`}
        </Typography>
        
        {/* Job Description */}
        <Typography 
          variant="body1" 
          sx={{ 
            whiteSpace: 'pre-wrap',
            lineHeight: 1.4,
            fontSize: '14px',
            color: 'text.primary',
            textAlign: 'left',
            mb: 1
          }}
        >
          {job.description}
        </Typography>
        
        
        {/* Message timestamp */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'text.secondary',
              fontSize: '11px'
            }}
          >
            {job.createdAt ? formatWhatsAppTime(job.createdAt) : 'Just now'}
          </Typography>
        </Box>
      </Box>
      
      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, alignItems: 'center' }}>
        <IconButton
          onClick={handleShareClick}
          size="small"
          sx={{
            color: 'primary.main',
            '&:hover': {
              backgroundColor: (t) => t.palette.mode === 'dark' ? 'hsla(145, 72%, 40%, 0.1)' : t.palette.primary[50]
            }
          }}
        >
          <ShareIcon fontSize="small" />
        </IconButton>
        
        <Button
          variant="contained"
          color="primary"
          onClick={handleApply}
          startIcon={<ArrowForwardIcon />}
          sx={{
            borderRadius: '20px',
            textTransform: 'none',
            fontSize: '14px',
            px: 3
          }}
        >
          Apply Now
        </Button>
      </Box>
      
      <ShareMenu
        job={job}
        anchorEl={shareAnchorEl}
        open={Boolean(shareAnchorEl)}
        onClose={handleShareClose}
        onSnackbar={handleSnackbar}
      />
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      <Dialog open={suspendedDialogOpen} onClose={() => setSuspendedDialogOpen(false)}>
        <DialogTitle>Account Suspended</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your account has been suspended and you cannot apply for jobs at this time. Please contact support for more information.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuspendedDialogOpen(false)} autoFocus>OK</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// WebApp job card removed — we now render WhatsApp-style cards only.

// Main Jobs Component
const JobsMUI = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { userTypeData } = useUserType();
  const isSuspended = userTypeData?.profiles?.jobSeeker?.accountStatus === 'suspended';
  const navigate = useNavigate();
  const posthog = usePostHog();

  useEffect(() => {
    posthog?.capture('job_board_viewed', { board: 'classic' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const jobService = useMemo(() => new JobService(), []);

  useEffect(() => {
    const fetchJobs = async () => {
      const CACHE_KEY = 'all_jobs';

      // Check if we have cached data
      const cachedJobs = jobsCache.get(CACHE_KEY);
      if (cachedJobs) {
        setJobs(cachedJobs);
        setLoading(false);
        return;
      }

      // No cached data, fetch from API
      setLoading(true);
      setError('');
      try {
        const result = await jobService.getAllJobs();
        const jobsData = result.items || [];
        
        // Store in cache
        jobsCache.set(CACHE_KEY, jobsData);
        
        setJobs(jobsData);
      } catch (err) {
        console.error('Error fetching jobs:', err);
        setError('Failed to load jobs. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sort all jobs by creation date in descending order (newest first)
  const sortedJobs = jobs.sort((a, b) => {
    const dateA = new Date(a.createdAt || 0);
    const dateB = new Date(b.createdAt || 0);
    return dateB - dateA;
  });

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="50vh">
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading jobs...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ backgroundColor: 'background.default', minHeight: '100vh', py: 4 }}>
        <Container maxWidth="lg">
          {/* New Board Banner */}
          <Alert
            severity="warning"
            sx={{ mb: 3, borderRadius: 2 }}
            action={
              <Button
                size="small"
                color="inherit"
                variant="outlined"
                onClick={() => {
                  posthog?.capture('job_board_switched', { from: 'classic', to: 'revamp' });
                  navigate('/jobs');
                }}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Try New Board
              </Button>
            }
          >
            <strong>⚠️ This job board will be deprecated soon.</strong> The new and improved Job Board is now available!
          </Alert>

          <Typography
            variant="h6"
            component="h1"
            gutterBottom
            textAlign="center"
            fontWeight="bold"
            color="primary.main"
            sx={{ mb: 4 }}
          >
            Available Jobs
          </Typography>
          
          {/* All Jobs Section */}
          {sortedJobs.length > 0 && (
            <Grid container spacing={3}>
              {sortedJobs.map(job => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={job.id}>
                  <WhatsAppJobCard job={job} isSuspended={isSuspended} />
                </Grid>
              ))}
            </Grid>
          )}
          
          {/* No jobs message */}
          {sortedJobs.length === 0 && (
            <Box textAlign="center" py={8}>
              <WorkIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h5" color="text.secondary" gutterBottom>
                No jobs available at the moment
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Check back later for new opportunities!
              </Typography>
            </Box>
          )}
        </Container>
    </Box>
  );
};

export default JobsMUI;